import { h } from '../lib/dom.js';
import { money, pct } from '../lib/format.js';
import { getListing, deleteListing } from '../lib/storage.js';
import { navigate } from '../lib/router.js';
import { ParcelMap } from '../components/parcel-map.js';
import { polygonCentroid } from '../../shared/geometry.js';
import { assessSubdivision, verdictPresentation } from '../../shared/subdivision.js';

export function FinderDetailPage({ params }) {
  const listing = getListing(params.id);
  if (!listing) {
    return h('div', { class: 'max-w-[1200px] mx-auto px-6 md:px-10 py-16' },
      h('p', { class: 'text-[color:var(--color-ink-dim)]' }, 'Listing not found.'),
      h('a', { href: '#/finder', class: 'btn btn-ghost mt-4' }, '← Back to finder'),
    );
  }

  const sappa = listing.sappa;
  const parcel = sappa?.parcel ?? null;
  const zone = sappa?.zone ?? null;
  const overlays = Array.isArray(sappa?.overlays) ? sappa.overlays : [];
  const tnvs = Array.isArray(sappa?.tnvs) ? sappa.tnvs : [];
  const overlaySummary = sappa?.overlay_summary ?? {};
  const valuation = sappa?.valuation ?? null;

  const verdict = assessSubdivision({
    area_m2: parcel?.area_m2 ?? listing.land_m2 ?? null,
    frontage_m: parcel?.frontage_m ?? listing.frontage_m ?? null,
    zone_code: zone?.code ?? null,
    zone_name: zone?.name ?? listing.parcel?.zone ?? null,
    zone_category: zone?.category ?? null,
    overlay_summary: overlaySummary,
    overlays,
    is_corner: parcel?.is_corner ?? false,
  });

  const score = listing.score?.score ?? 0;
  const poc = listing.feaso_preview?.profit_on_cost_pct ?? 0;
  const signal =
    score >= 70 ? 'text-[color:var(--color-positive)]'
    : score >= 40 ? 'text-[color:var(--color-warning)]'
    : 'text-[color:var(--color-negative)]';

  return h('div', { class: 'max-w-[1200px] mx-auto px-6 md:px-10' },
    // Folio header
    h('header', { class: 'pt-10 pb-6 border-b border-[color:var(--color-rule)]' },
      h('div', { class: 'folio' }, 'Finder · Listing'),
      h('div', { class: 'flex items-end justify-between gap-6 flex-wrap mt-1' },
        h('h1', { class: 'display display-md max-w-[36ch]' }, listing.address),
        actionsRow(listing, parcel, sappa),
      ),
    ),

    // Verdict — the lead answer to "can it be subdivided, and how many?"
    verdictPanel(verdict),

    // Top row: score + quick stats
    h('section', { class: 'grid md:grid-cols-12 gap-10 py-8 border-b border-[color:var(--color-rule)]' },
      h('div', { class: 'md:col-span-5' },
        h('div', { class: 'folio' }, 'Score'),
        h('div', { class: `display display-lg num mt-2 ${signal}` },
          String(score).padStart(2, '0'),
          h('span', { class: 'text-[color:var(--color-ink-faint)]' }, ' / ', listing.score?.maxScore ?? 100),
        ),
      ),
      h('div', { class: 'md:col-span-7 grid grid-cols-2 gap-6' },
        stat('Asking', money(listing.price ?? 0)),
        stat('Target', `${listing.target_dwellings} × ${listing.sale_price_per_dwelling ? money(listing.sale_price_per_dwelling) : '—'}`),
        stat('Land', listing.land_m2 ? `${listing.land_m2} m²` : '—'),
        stat('Frontage', listing.frontage_m ? `${listing.frontage_m} m` : '—'),
        stat('Zone', zone?.name || listing.parcel?.zone || '—'),
        stat('Corner', parcel?.is_corner ? 'Yes' : parcel?.is_corner === false ? 'No' : '—'),
      ),
    ),

    // Map + facts, side by side
    parcel?.polygon_wgs84 ? h('section', { class: 'grid md:grid-cols-12 gap-8 py-10 border-b border-[color:var(--color-rule)]' },
      h('div', { class: 'md:col-span-7 space-y-3' },
        h('div', { class: 'folio' }, 'Parcel map'),
        ParcelMap({ geojson: parcel.polygon_wgs84, neighbors: sappa?.neighbors ?? [], height: 420 }),
        h('div', { class: 'text-[0.72rem] text-[color:var(--color-ink-faint)] italic' },
          parcel.perimeter_m ? `Perimeter ${Math.round(parcel.perimeter_m)} m` : null,
          parcel.road_names?.length ? ` · Touching: ${parcel.road_names.join(', ')}` : null,
        ),
      ),
      h('div', { class: 'md:col-span-5 space-y-4' },
        h('div', { class: 'folio' }, 'Property facts'),
        fact('Plan parcel', parcel.plan_parcel),
        fact('Title ref', parcel.title_ref),
        fact('Valuation no', valuation?.number),
        fact('Hundred', parcel.address_parts?.hundred),
        fact('Suburb', parcel.address_parts?.suburb),
        fact('Area', parcel.area_m2 ? `${Math.round(parcel.area_m2)} m²` : null),
        fact('Perimeter', parcel.perimeter_m ? `${Math.round(parcel.perimeter_m)} m` : null),
        fact('Roads touching', parcel.distinct_road_count != null ? String(parcel.distinct_road_count) : null),
        fact('Zone code', zone?.code),
      ),
    ) : null,

    // Planning zones + variations + overlays
    sappa ? h('section', { class: 'py-10 border-b border-[color:var(--color-rule)] space-y-10' },
      h('div', { class: 'grid md:grid-cols-12 gap-8' },
        h('div', { class: 'md:col-span-4' },
          h('h2', { class: 'display display-sm' }, 'Planning'),
          h('p', { class: 'text-sm text-[color:var(--color-ink-dim)] mt-2' },
            'Zones set the base rules; Variations (TNV) override them for this specific parcel; Overlays layer additional constraints on top.',
          ),
        ),
        h('div', { class: 'md:col-span-8 space-y-8' },
          // Zones
          h('div', null,
            h('div', { class: 'folio mb-3' }, `Zones${zone ? '' : ' — none'}`),
            zone ? h('div', { class: 'space-y-0.5' },
              h('div', { class: 'display display-sm' }, `${zone.name}${zone.code ? ` — ${zone.code}` : ''}`),
              zone.category ? h('div', { class: 'text-sm text-[color:var(--color-ink-dim)]' }, zone.category, ' · parent category') : null,
            ) : null,
          ),
          // Variations
          tnvs.length ? h('div', null,
            h('div', { class: 'folio mb-3' }, `Variations · ${tnvs.length}`),
            h('ul', { class: 'space-y-1' }, ...tnvs.map(tnvRow)),
          ) : null,
          // Overlays
          overlays.length ? h('div', null,
            h('div', { class: 'folio mb-3' }, `Overlays · ${overlays.length}`),
            h('ul', { class: 'space-y-3' }, ...overlays.map(overlayRow)),
          ) : null,
        ),
      ),
    ) : null,

    // Score breakdown
    h('section', { class: 'grid md:grid-cols-12 gap-10 py-10 border-b border-[color:var(--color-rule)]' },
      h('div', { class: 'md:col-span-5' },
        h('h2', { class: 'display display-sm' }, 'Score breakdown'),
        h('p', { class: 'text-sm text-[color:var(--color-ink-dim)] mt-2' },
          'Every listing gets the same test. Missing signals show as em dash and score zero until the data is filled in.',
        ),
      ),
      h('ul', { class: 'md:col-span-7 space-y-1' },
        ...((listing.score?.breakdown ?? []).map((b) => breakdownRow(b))),
      ),
    ),

    // Auto-feasibility preview
    listing.feaso_preview ? h('section', { class: 'grid md:grid-cols-12 gap-10 py-10 border-b border-[color:var(--color-rule)]' },
      h('div', { class: 'md:col-span-5' },
        h('h2', { class: 'display display-sm' }, 'Auto-feasibility'),
        h('p', { class: 'text-sm text-[color:var(--color-ink-dim)] mt-2' },
          'Computed with your library defaults. For a refined number, open the full feasibility above.',
        ),
      ),
      h('div', { class: 'md:col-span-7 space-y-1' },
        kv('Forecast profit', money(listing.feaso_preview.forecast_profit), true),
        kv('Profit on cost', pct(poc), true),
        kv('Net sales proceeds', money(listing.feaso_preview.net_sales)),
      ),
    ) : null,

    // Notes
    listing.notes ? h('section', { class: 'py-10 border-b border-[color:var(--color-rule)]' },
      h('div', { class: 'folio mb-3' }, 'Notes'),
      h('p', { class: 'max-w-[64ch] whitespace-pre-wrap' }, listing.notes),
    ) : null,

    // Footer
    h('footer', { class: 'py-8 flex justify-between items-center' },
      h('div', { class: 'folio text-[color:var(--color-ink-faint)]' },
        'Added ', new Date(listing.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
      ),
      h('button', {
        type: 'button',
        class: 'btn btn-ghost text-[color:var(--color-negative)] border-[color:var(--color-negative)]/30',
        onclick: () => {
          if (confirm(`Remove "${listing.address}" from the finder?`)) {
            deleteListing(listing.id);
            navigate('/finder');
          }
        },
      }, 'Remove listing'),
    ),
  );
}

function actionsRow(listing, parcel, sappa) {
  const items = [];

  // Run full feasibility
  items.push(h('a', {
    href: `#/feaso?address=${encodeURIComponent(listing.address)}&purchase_price=${listing.price}&dwellings=${listing.target_dwellings}&sale_price_per_dwelling=${listing.sale_price_per_dwelling ?? ''}`,
    class: 'btn btn-ink',
  }, 'Full feasibility →'));

  // Google Maps — prefer lat/lon from SAPPA; fall back to address search
  const centroid = parcel?.polygon_wgs84 ? polygonCentroid(parcel.polygon_wgs84) : null;
  const gmapsUrl = centroid
    ? `https://www.google.com/maps/search/?api=1&query=${centroid[1]},${centroid[0]}`
    : sappa?.location
      ? `https://www.google.com/maps/search/?api=1&query=${sappa.location.lat},${sappa.location.lon}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(listing.address)}`;
  items.push(h('a', {
    href: gmapsUrl, target: '_blank', rel: 'noopener',
    class: 'btn btn-ghost',
  }, 'Maps ↗'));

  // View on SAPPA
  items.push(h('a', {
    href: 'https://sappa.plan.sa.gov.au/',
    target: '_blank', rel: 'noopener',
    class: 'btn btn-ghost',
  }, 'SAPPA ↗'));

  // Original listing URL
  if (listing.url) {
    items.push(h('a', {
      href: listing.url, target: '_blank', rel: 'noopener',
      class: 'btn btn-ghost',
    }, 'Listing ↗'));
  }

  return h('div', { class: 'flex gap-2 flex-wrap' }, ...items);
}

function verdictPanel(verdict) {
  const { label, tone } = verdictPresentation(verdict.verdict);
  const toneCls = {
    positive: 'text-[color:var(--color-positive)]',
    warning:  'text-[color:var(--color-warning)]',
    negative: 'text-[color:var(--color-negative)]',
    neutral:  'text-[color:var(--color-ink-soft)]',
  }[tone];
  const bgCls = {
    positive: 'bg-[color:var(--color-olive-50)] border-[color:var(--color-olive-500)]',
    warning:  'bg-[#fdf6e8] border-[color:var(--color-warning)]',
    negative: 'bg-[#fbeded] border-[color:var(--color-negative)]',
    neutral:  'border-[color:var(--color-rule)]',
  }[tone];

  const dwellings = verdict.max_dwellings ?? {};
  const headline =
    verdict.verdict === 'not_residential' ? 'Not a residential zone'
    : verdict.verdict === 'unknown' ? 'Insufficient data'
    : verdict.verdict === 'blocked' ? 'Subdivision blocked'
    : `1 → ${Math.max(dwellings.detached ?? 0, dwellings.attached ?? 0) || '?'} dwellings${verdict.overlays?.some(o => o.severity === 'hard') ? ' (with major caveats)' : ''}`;

  return h('section', { class: `py-8 border-b border-l-4 ${bgCls} pl-6 pr-4` },
    h('div', { class: 'grid md:grid-cols-12 gap-6' },
      h('div', { class: 'md:col-span-5' },
        h('div', { class: 'folio' }, 'Subdivision verdict'),
        h('div', { class: `display display-md num mt-1 ${toneCls}` }, headline),
        h('div', { class: `uppercase text-xs tracking-widest mt-2 ${toneCls}` }, label),
      ),
      h('div', { class: 'md:col-span-4' },
        h('div', { class: 'folio' }, 'Most realistic counts'),
        h('ul', { class: 'mt-1 space-y-0.5 text-sm' },
          h('li', null, h('span', { class: 'folio mr-2' }, 'Detached'), h('span', { class: 'num font-medium' }, String(dwellings.detached ?? '—'))),
          h('li', null, h('span', { class: 'folio mr-2' }, 'Attached'), h('span', { class: 'num font-medium' }, String(dwellings.attached ?? '—'))),
        ),
        verdict.effective_min_site_per_dwelling ? h('div', { class: 'text-[0.72rem] text-[color:var(--color-ink-faint)] italic mt-1' },
          `Based on ${verdict.effective_min_site_per_dwelling} m² per dwelling (${verdict.effective_min_site_source})`,
        ) : null,
      ),
      h('div', { class: 'md:col-span-3' },
        h('div', { class: 'folio' }, 'Why'),
        h('ul', { class: 'mt-1 space-y-1 text-[0.8rem]' },
          ...(verdict.notes ?? []).slice(0, 3).map((n) => h('li', { class: 'text-[color:var(--color-ink-soft)]' }, n)),
        ),
      ),
    ),
    verdict.per_form ? h('div', { class: 'mt-6 grid md:grid-cols-2 gap-6 pt-4 border-t border-[color:var(--color-rule)]' },
      ...['detached', 'attached'].map((form) => {
        const pf = verdict.per_form[form];
        if (!pf) return null;
        return h('div', null,
          h('div', { class: 'folio mb-2' }, form),
          h('ul', { class: 'space-y-1' },
            ...(pf.checks ?? []).map((c) => h('li', { class: `leader py-1 text-sm ${c.passes ? 'text-[color:var(--color-positive)]' : c.area_ok ? 'text-[color:var(--color-ink-soft)]' : 'text-[color:var(--color-ink-faint)]'}` },
              h('span', null, c.passes ? '●' : '○', ' 1 → ', c.n),
              h('span', { class: 'dots' }),
              h('span', { class: 'num text-xs shrink-0' },
                `need ${c.area_required} m²${c.frontage_required ? ` · ${c.frontage_required} m front` : ''}`,
              ),
            )),
          ),
        );
      }),
    ) : null,
  );
}

function stat(label, value) {
  return h('div', null,
    h('div', { class: 'folio' }, label),
    h('div', { class: 'display display-sm num mt-1' }, value),
  );
}

function fact(label, value) {
  if (value == null || value === '') return null;
  return h('div', { class: 'leader py-1.5 border-b border-[color:var(--color-rule-soft)]' },
    h('span', { class: 'text-sm text-[color:var(--color-ink-soft)]' }, label),
    h('span', { class: 'dots' }),
    h('span', { class: 'num text-sm shrink-0' }, String(value)),
  );
}

function kv(label, value, strong) {
  return h('div', { class: `leader py-2 ${strong ? 'font-medium' : ''}` },
    h('span', null, label),
    h('span', { class: 'dots' }),
    h('span', { class: `num shrink-0 ${strong ? 'display display-sm' : ''}` }, value),
  );
}

function tnvRow(t) {
  // TNV description often restates the value; prefer the summary we
  // extracted (`t.value` was either an explicit field or parsed from text).
  return h('li', { class: 'leader py-2 border-b border-[color:var(--color-rule-soft)]' },
    h('span', { class: 'min-w-0' },
      h('span', null, t.name),
      t.description
        ? h('span', { class: 'block text-[0.72rem] text-[color:var(--color-ink-faint)] italic' }, t.description)
        : null,
    ),
    h('span', { class: 'dots' }),
    h('span', { class: 'num shrink-0 text-sm' }, String(t.value ?? '—')),
  );
}

function overlayRow(o) {
  return h('li', { class: 'py-2 border-b border-[color:var(--color-rule-soft)]' },
    h('div', { class: 'flex items-baseline justify-between gap-4' },
      h('div', { class: 'min-w-0' },
        h('div', { class: 'font-medium' }, o.name, o.code ? h('span', { class: 'text-[color:var(--color-ink-faint)] ml-2 num text-xs' }, o.code) : null),
        o.value
          ? h('div', { class: 'text-sm text-[color:var(--color-ink-soft)] mt-0.5' }, String(o.value))
          : null,
      ),
    ),
    o.description
      ? h('p', { class: 'text-[0.8rem] text-[color:var(--color-ink-dim)] mt-1.5 max-w-[70ch]' }, o.description)
      : null,
  );
}

function breakdownRow(b) {
  const indicator = b.met === true ? '●' : b.met === false ? '○' : '—';
  const color =
    b.met === true ? 'text-[color:var(--color-positive)]'
    : 'text-[color:var(--color-ink-faint)]';

  return h('li', { class: 'leader py-2 border-b border-[color:var(--color-rule-soft)]' },
    h('span', { class: 'min-w-0' },
      h('span', { class: `${color} mr-2 num` }, indicator),
      h('span', null, b.label),
      b.note ? h('span', { class: 'block text-[0.72rem] text-[color:var(--color-ink-faint)] italic ml-5' }, b.note) : null,
    ),
    h('span', { class: 'dots' }),
    h('span', { class: 'num shrink-0 display display-sm' }, `+${b.points}`),
  );
}
